import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { tokenStore } from '@/lib/auth';
import { Spinner } from '@/components/ui/Spinner';
import { BillerBayLogo } from '@/components/ui/BillerBayLogo';
import { usePageTitle } from '@/hooks/usePageTitle';

/**
 * Handles the redirect back from Microsoft SSO. The backend issues a
 * short-lived one-time code in the query string:
 *
 *   /auth/sso-callback?code=…
 *
 * This page redeems the code via POST /api/auth/microsoft/redeem, stores
 * the returned tokens in the auth layer, hydrates the user, and navigates
 * to the home page.
 */
export function SSOCallbackPage() {
  usePageTitle('Signing in…');
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Backend may deliver tokens via hash fragment (#access_token=...&refresh_token=...)
    // or via one-time code query param (?code=...). Handle both.
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');

    if (accessToken && refreshToken) {
      import('@/lib/api').then(({ api }) => {
        tokenStore.setAccess(accessToken);
        tokenStore.setRefresh(refreshToken);
        api.get('/users/me').then((resp) => {
          setSession({ access_token: accessToken, refresh_token: refreshToken, user: resp.data });
          window.history.replaceState(null, '', '/');
          navigate('/', { replace: true });
        }).catch(() => {
          setError('Failed to complete sign-in. Please try again.');
        });
      });
      return;
    }

    // One-time code flow
    const code = searchParams.get('code');
    if (!code) {
      setError('Missing SSO code in the callback. Please try again.');
      return;
    }

    import('@/lib/api').then(({ api }) => {
      api
        .post('/auth/microsoft/redeem', { code })
        .then((resp) => {
          const { access_token: at, refresh_token: rt, user } = resp.data;
          tokenStore.setAccess(at);
          tokenStore.setRefresh(rt);
          setSession({ access_token: at, refresh_token: rt, user });
          window.history.replaceState(null, '', '/');
          navigate('/', { replace: true });
        })
        .catch(() => {
          setError('Failed to complete sign-in. The link may have expired — please try again.');
        });
    });
  }, [navigate, setSession, searchParams]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bb-bg p-4">
        <div className="w-full max-w-md bg-white shadow-sm border border-bb-lilac/40 p-8 text-center">
          <BillerBayLogo variant="dark" height={28} className="mx-auto mb-4" />
          <h1 className="text-lg font-bold text-red-700 mb-2">Sign-in failed</h1>
          <p className="text-sm text-slate-600 mb-4">{error}</p>
          <a
            href="/login"
            className="text-sm text-bb-navy font-medium hover:underline"
          >
            Back to sign in
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bb-bg">
      <div className="text-center">
        <Spinner size={28} />
        <p className="text-sm text-slate-500 mt-3">Completing Microsoft sign-in…</p>
      </div>
    </div>
  );
}
